<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import ConversationApi from 'dashboard/api/inbox/conversation';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  accountId: { type: Number, required: true },
});
const emit = defineEmits(['statusChange']);
const { t } = useI18n();
const router = useRouter();
const store = useStore();
const confirmation = ref(null);
const status = ref({});
const isUpdating = ref(false);
const { run } = useAbortableRequest();

const action = computed(() => {
  if (status.value.can_transfer) return 'transfer';
  if (status.value.can_return) return 'return';
  return null;
});
const copy = computed(() => {
  if (action.value === 'return') {
    return {
      label: t('CONVERSATION.TEAM_HANDOFF.RETURN.LABEL'),
      title: t('CONVERSATION.TEAM_HANDOFF.RETURN.TITLE'),
      description: t('CONVERSATION.TEAM_HANDOFF.RETURN.DESCRIPTION'),
    };
  }

  return {
    label: t('CONVERSATION.TEAM_HANDOFF.TRANSFER.LABEL'),
    title: t('CONVERSATION.TEAM_HANDOFF.TRANSFER.TITLE'),
    description: t('CONVERSATION.TEAM_HANDOFF.TRANSFER.DESCRIPTION'),
  };
});

watch(
  () => props.conversationId,
  async conversationId => {
    status.value = {};
    emit('statusChange', status.value);
    try {
      const response = await run(signal =>
        ConversationApi.getTeamHandoff(conversationId, { signal })
      );
      if (!response) return;
      status.value = response.data;
      emit('statusChange', status.value);
    } catch {
      status.value = {};
      emit('statusChange', status.value);
    }
  },
  { immediate: true }
);

const update = async () => {
  const conversationId = props.conversationId;
  const accountId = props.accountId;
  const requestedAction = action.value;
  if (!(await confirmation.value.showConfirmation())) return;
  if (
    conversationId !== props.conversationId ||
    requestedAction !== action.value
  )
    return;

  isUpdating.value = true;
  try {
    if (requestedAction === 'transfer')
      await ConversationApi.transferToPostSales(conversationId);
    else await ConversationApi.returnToCommercial(conversationId);

    if (conversationId === props.conversationId)
      await router.replace({ name: 'home', params: { accountId } });
    await store.dispatch('removeConversationCache', conversationId);
    useAlert(t('CONVERSATION.TEAM_HANDOFF.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('CONVERSATION.TEAM_HANDOFF.ERROR')
    );
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <div>
    <div v-if="action" class="pt-3">
      <Button
        faded
        class="w-full"
        icon="i-lucide-arrow-right-left"
        :label="copy.label"
        :is-loading="isUpdating"
        @click="update"
      />
      <ConfirmModal
        ref="confirmation"
        :title="copy.title"
        :description="copy.description"
        :confirm-label="copy.label"
        :cancel-label="t('CONVERSATION.TEAM_HANDOFF.CANCEL')"
      />
    </div>
  </div>
</template>
